<template>
  <div class="ingredients-balance">
    <div class="balance-header">
      <h4>Balance de Ingredientes</h4>
      <button
        v-if="ingredients.length > 0"
        class="expand-btn"
        @click="showModal = true"
        title="Ver detalle completo"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="currentColor"
        >
          <path d="M1.5 1a.5.5 0 0 0-.5.5v4a.5.5 0 0 1-1 0v-4A1.5 1.5 0 0 1 1.5 0h4a.5.5 0 0 1 0 1h-4zM10 .5a.5.5 0 0 1 .5-.5h4A1.5 1.5 0 0 1 16 1.5v4a.5.5 0 0 1-1 0v-4a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 1-.5-.5zM.5 10a.5.5 0 0 1 .5.5v4a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 0 14.5v-4a.5.5 0 0 1 .5-.5zm15 0a.5.5 0 0 1 .5.5v4a1.5 1.5 0 0 1-1.5 1.5h-4a.5.5 0 0 1 0-1h4a.5.5 0 0 0 .5-.5v-4a.5.5 0 0 1 .5-.5z" />
        </svg>
      </button>
    </div>

    <div
      v-if="loading"
      class="loading"
    >
      <div class="spinner" />
      <p>Cargando balance...</p>
    </div>
    <div
      v-else-if="error"
      class="error"
    >
      {{ error }}
    </div>
    <div
      v-else-if="ingredients.length === 0"
      class="empty-state"
    >
      No hay ingredientes para mostrar
    </div>
    <div
      v-else
      class="summary-info"
    >
      <p><strong>{{ ingredients.length }}</strong> ingredientes</p>
      <p class="total-summary">
        Total: <strong>{{ formatNumber(totalProduction) }} kg</strong>
      </p>
    </div>

    <!-- Modal -->
    <div
      v-if="showModal"
      class="modal-overlay"
      @click="showModal = false"
    >
      <div
        class="modal-content"
        @click.stop
      >
        <div class="modal-header">
          <h3>Balance de Ingredientes</h3>
          <button
            class="close-btn"
            @click="showModal = false"
          >
            ×
          </button>
        </div>
        <div class="modal-body">
          <div
            v-if="loading"
            class="modal-loading"
          >
            <div class="spinner-large" />
            <p>Cargando balance de ingredientes...</p>
          </div>
          <table
            v-else
            class="balance-table-modal"
          >
            <thead>
              <tr>
                <th>Ingredientes</th>
                <th>Producción (kg)</th>
                <th>Balance (kg)</th>
                <th>Saldo (kg)</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="ingredient in sortedIngredients"
                :key="ingredient.id"
              >
                <td class="ingredient-name">
                  {{ ingredient.name }}
                </td>
                <td class="numeric">
                  {{ formatNumber(ingredient.production) }}
                </td>
                <td class="numeric">
                  {{ formatNumber(ingredient.balance) }}
                </td>
                <td
                  class="numeric"
                  :class="{ 'negative-balance': ingredient.saldo < 0 }"
                >
                  {{ formatNumber(ingredient.saldo) }}
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="total-row">
                <td>
                  <strong>Total producciones</strong>
                </td>
                <td class="numeric">
                  <strong>{{ formatNumber(totalProduction) }}</strong>
                </td>
                <td colspan="2" />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'IngredientsBalance',
  props: {
    visibleOrderIds: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      ingredients: [],
      loading: false,
      error: null,
      showModal: false,
    };
  },
  computed: {
    sortedIngredients() {
      if (!Array.isArray(this.ingredients)) return [];

      // Sort by production amount (descending)
      return [...this.ingredients].sort((a, b) => {
        const prodA = a.production || 0;
        const prodB = b.production || 0;

        return prodB - prodA;
      });
    },
    totalProduction() {
      if (!Array.isArray(this.ingredients)) return 0;

      return this.ingredients.reduce((sum, ing) => sum + (ing.production || 0), 0);
    },
  },
  watch: {
    visibleOrderIds: {
      handler(newIds, oldIds) {
        // Solo actualizar si realmente cambiaron los IDs
        const newIdsStr = JSON.stringify(newIds.sort());
        const oldIdsStr = JSON.stringify((oldIds || []).sort());
        if (newIdsStr !== oldIdsStr) {
          this.fetchIngredientsBalance();
        }
      },
      deep: true,
    },
  },
  mounted() {
    // Esperar un momento para que el calendario cargue los eventos
    const INITIAL_LOAD_DELAY = 500;
    setTimeout(() => {
      this.fetchIngredientsBalance();
    }, INITIAL_LOAD_DELAY);
  },
  methods: {
    async fetchIngredientsBalance() {
      if (this.visibleOrderIds.length === 0) {
        this.ingredients = [];

        return;
      }

      this.loading = true;
      this.error = null;

      try {
        await this.loadBalanceData();
      } catch (err) {
        this.error = err.message;
        console.error('Error fetching ingredients balance:', err);
      } finally {
        this.loading = false;
      }
    },
    async loadBalanceData() {
      const params = new URLSearchParams();
      this.visibleOrderIds.forEach(id => params.append('order_ids[]', id));

      const response = await fetch(`/api/v1/ingredients_balance?${params.toString()}`);
      if (!response.ok) {
        throw new Error('Error al cargar el balance de ingredientes');
      }

      const data = await response.json();
      this.ingredients = data.ingredients || [];
    },
    formatNumber(value) {
      if (value === null || value === undefined) return '-';

      return new Intl.NumberFormat('es-CL').format(Math.round(value));
    },
  },
};
</script>

<style scoped>
.ingredients-balance {
  margin-top: 1rem;
  padding: 1rem;
  background: #fff;
  border: 1px solid #d3d8dc;
  border-radius: 0;
  width: 100%;
  max-width: 270px;
  font-family: 'Source Sans Pro', sans-serif;
  box-sizing: border-box;
}

.balance-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.ingredients-balance h4 {
  margin: 0;
  font-size: 1em;
  font-weight: bold;
  color: #4e4e51;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: 'Source Sans Pro', sans-serif;
}

.expand-btn {
  background: #4e4e51;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.expand-btn:hover {
  background: #3a3a3d;
}

.expand-btn svg {
  display: block;
}

.loading,
.error,
.empty-state {
  text-align: center;
  padding: 1rem;
  font-size: 0.85rem;
  color: #6c757d;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.spinner {
  width: 24px;
  height: 24px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #4e4e51;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error {
  color: #dc3545;
}

.empty-state {
  font-style: italic;
}

.summary-info {
  padding: 0.5rem 0;
}

.summary-info p {
  margin: 0.25rem 0;
  font-size: 0.85rem;
  color: #4e4e51;
}

.total-summary {
  color: #28a745;
  font-size: 0.9rem;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e0e0e0;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.5rem;
  color: #4e4e51;
  font-family: 'Source Sans Pro', sans-serif;
}

.close-btn {
  background: none;
  border: none;
  font-size: 2rem;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #333;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.modal-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;
}

.modal-loading p {
  margin: 0;
  color: #6c757d;
  font-size: 1rem;
}

.spinner-large {
  width: 48px;
  height: 48px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4e4e51;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.balance-table-modal {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
}

.balance-table-modal th {
  background: #f8f9fa;
  padding: 0.75rem 1rem;
  text-align: left;
  font-weight: 600;
  color: #4e4e51;
  border-bottom: 2px solid #dee2e6;
  font-size: 0.9rem;
  white-space: nowrap;
}

.balance-table-modal th:first-child {
  text-align: left;
  width: 40%;
}

.balance-table-modal th:nth-child(2),
.balance-table-modal th:nth-child(3),
.balance-table-modal th:nth-child(4) {
  text-align: right;
  width: 20%;
}

.balance-table-modal td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e9ecef;
  font-size: 0.9rem;
}

.balance-table-modal tbody tr:hover {
  background: #f8f9fa;
}

.balance-table-modal .ingredient-name {
  font-weight: 500;
  color: #212529;
}

.balance-table-modal .numeric {
  text-align: right;
  font-family: monospace;
  font-size: 0.9rem;
}

.balance-table-modal .negative-balance {
  color: #dc3545;
  font-weight: 700;
}

.balance-table-modal .total-row {
  background: #f8f9fa;
}

.balance-table-modal .total-row td {
  border-top: 2px solid #4e4e51;
  border-bottom: 2px solid #4e4e51;
  padding: 1rem;
  font-weight: 600;
  font-size: 1rem;
}

.balance-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75rem;
  table-layout: fixed;
}

.balance-table th {
  background: #f8f9fa;
  padding: 0.4rem 0.3rem;
  text-align: left;
  font-weight: 600;
  color: #4e4e51;
  border-bottom: 2px solid #dee2e6;
  font-size: 0.7rem;
  white-space: nowrap;
}

.balance-table th:first-child {
  text-align: left;
  width: 35%;
}

.balance-table th:nth-child(2),
.balance-table th:nth-child(3),
.balance-table th:nth-child(4) {
  text-align: right;
  width: 21.66%;
}

.balance-table td {
  padding: 0.3rem 0.3rem;
  border-bottom: 1px solid #e9ecef;
  font-size: 0.7rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.balance-table tbody tr:hover {
  background: #f8f9fa;
}

.ingredient-name {
  font-weight: 500;
  color: #212529;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.numeric {
  text-align: right;
  font-family: monospace;
  font-size: 0.7rem;
}

.negative-balance {
  color: #dc3545;
  font-weight: 700;
}

.total-row {
  background: #f8f9fa;
}

.total-row td {
  border-top: 2px solid #4e4e51;
  border-bottom: 2px solid #4e4e51;
  padding: 0.5rem 0.3rem;
  font-weight: 600;
}
</style>
